import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, LocationId, ActionCompletionToast } from '../game/types';

export class MovementSystem {
  constructor(
    private gameState: GameStateManager,
    private gameData: GameData
  ) {}

  getMovementCost(from: LocationId, to: LocationId): number | null {
    const fromLocation = this.gameData.locations.find(l => l.id === from);
    if (!fromLocation) return null;

    const connection = fromLocation.connections.find(c => c.to === to);
    return connection ? connection.cost : null;
  }

  canMove(researcher: Researcher, targetLocation: LocationId): boolean {
    // Can't move if already at target
    if (researcher.location === targetLocation) return false;

    // CRITICAL: Cannot move while containing - containment must not be interrupted
    if (researcher.currentAction === 'Contain') return false;

    // Can always issue a new move order, even while moving
    return true;
  }

  findPath(from: LocationId, to: LocationId): LocationId[] | null {
    if (from === to) return [to];
    
    // Simple BFS pathfinding for linear connections
    const visited = new Set<LocationId>();
    const queue: { location: LocationId; path: LocationId[] }[] = [{ location: from, path: [from] }];
    
    while (queue.length > 0) {
      const { location, path } = queue.shift()!;
      
      if (location === to) {
        return path;
      }
      
      if (visited.has(location)) continue;
      visited.add(location);
      
      const locationData = this.gameData.locations.find(l => l.id === location);
      if (!locationData) continue;
      
      for (const connection of locationData.connections) {
        if (!visited.has(connection.to)) {
          queue.push({ location: connection.to, path: [...path, connection.to] });
        }
      }
    }
    
    return null; // No path found
  }

  startMovement(researcher: Researcher, targetLocation: LocationId): void {
    const currentLocation = researcher.location;
    
    if (!currentLocation) {
      // If researcher is between locations, use target as starting point
      if (researcher.targetLocation) {
        const path = this.findPath(researcher.targetLocation, targetLocation);
        if (path && path.length > 1) {
          // Store path for multi-step movement
          researcher.movementPath = path.slice(1); // Exclude current location
          researcher.location = researcher.targetLocation;
          researcher.targetLocation = researcher.movementPath[0];
          researcher.movementProgress = 0;
        }
      }
      return;
    }

    // If already moving, cancel and start new path
    if (researcher.targetLocation !== null) {
      researcher.targetLocation = null;
      researcher.movementProgress = 0;
      researcher.movementPath = undefined;
    }

    // Find path to target
    const path = this.findPath(currentLocation, targetLocation);
    
    if (!path || path.length < 2) {
      // Direct connection or already at target
      if (this.getMovementCost(currentLocation, targetLocation) !== null) {
        researcher.targetLocation = targetLocation;
        researcher.movementProgress = 0;
        researcher.movementPath = undefined;
      }
      return;
    }

    // Multi-step path
    // Path includes: [currentLocation, ...intermediateLocations, finalDestination]
    // We want movementPath to be: [...intermediateLocations, finalDestination] (excluding current)
    const movementPath = path.slice(1); // Exclude current location
    researcher.movementPath = movementPath;
    researcher.targetLocation = movementPath[0];
    researcher.movementProgress = 0;
  }

  updateMovement(researcher: Researcher): void {
    if (researcher.location === null) {
      return;
    }

    // Check if we need to start the next step in a multi-step path
    const movementPath = researcher.movementPath;
    if (researcher.targetLocation === null && movementPath && movementPath.length > 0) {
      // Start next step in path
      researcher.targetLocation = movementPath[0];
      researcher.movementPath = movementPath.slice(1);
      researcher.movementProgress = 0;
    }

    // Now check if we have a valid target
    if (researcher.targetLocation === null) {
      return;
    }

    let cost = this.getMovementCost(researcher.location, researcher.targetLocation);
    if (cost === null) {
      // Invalid movement, cancel
      researcher.targetLocation = null;
      researcher.movementProgress = 0;
      researcher.movementPath = undefined;
      return;
    }

    // Apply Fast_Runner ability (halve movement times)
    if (researcher.data.special_rules.includes('Fast_Runner')) {
      cost = cost / 2;
    }

    // Apply movementTimeMultiplier effect (Temporal Distortion)
    const multiplier = this.gameState.getEffectValue('movementTimeMultiplier');
    if (typeof multiplier === 'number') {
      cost = cost * multiplier;
    }

    // Increment progress (1 tick = 1 unit of progress)
    researcher.movementProgress += 1 / cost;

    // Check if movement complete
    if (researcher.movementProgress >= 1.0) {
      researcher.location = researcher.targetLocation;
      researcher.movementProgress = 0;
      
      // Check if there's a path to continue
      let remainingPath = researcher.movementPath;
      
      if (remainingPath && remainingPath.length > 0) {
        // Skip any locations in the path that match current location (shouldn't happen, but safety check)
        while (remainingPath.length > 0 && remainingPath[0] === researcher.location) {
          remainingPath = remainingPath.slice(1);
        }
        
        if (remainingPath.length > 0) {
          // Move to next step in path - set immediately to ensure continuation
          researcher.targetLocation = remainingPath[0];
          researcher.movementPath = remainingPath.slice(1);
        } else {
          // Path complete (all remaining steps were duplicates)
          researcher.targetLocation = null;
          researcher.movementPath = undefined;
          if (researcher.currentAction === 'Move') {
            // Generate arrival report toast
            const strings = this.gameData.uiStrings;
            const locationName = strings.locations?.[researcher.location!] || researcher.location;
            const toast: ActionCompletionToast = {
              researcherName: researcher.data.name,
              location: researcher.location!,
              actionType: 'Move',
              summary: `Arrived at ${locationName}`,
              timestamp: Date.now()
            };
            this.gameState.queueToast(toast);
            
            researcher.currentAction = null;
          }
        }
      } else {
        // Path complete
        researcher.targetLocation = null;
        researcher.movementPath = undefined;
        // Clear Move action when movement completes
        if (researcher.currentAction === 'Move') {
          // Generate arrival report toast
          const strings = this.gameData.uiStrings;
          const locationName = strings.locations?.[researcher.location!] || researcher.location;
          const toast: ActionCompletionToast = {
            researcherName: researcher.data.name,
            location: researcher.location!,
            actionType: 'Move',
            summary: `Arrived at ${locationName}`,
            timestamp: Date.now()
          };
          this.gameState.queueToast(toast);
          
          researcher.currentAction = null;
        }
      }
    }
  }

  getCurrentLocation(researcher: Researcher): LocationId | null {
    // If moving, researcher is between locations
    if (researcher.targetLocation !== null) {
      return null; // Between locations
    }
    return researcher.location;
  }

  calculateDistance(from: LocationId, to: LocationId): number {
    if (from === to) {
      return 0; // Same room - distance 0
    }
    
    const path = this.findPath(from, to);
    if (!path) {
      return 5; // No path found, treat as maximum distance (shouldn't happen)
    }
    
    // Path length minus 1 (excluding starting location)
    // Distance mapping: 1 step = 4, 2 steps = 3, 3 steps = 2, 4+ steps = 1
    const steps = path.length - 1;
    if (steps === 0) {
      return 0; // Same room - distance 0
    } else if (steps === 1) {
      return 4; // Adjacent
    } else if (steps === 2) {
      return 3; // 2 rooms away
    } else if (steps === 3) {
      return 2; // 3 rooms away
    } else {
      return 1; // 4+ rooms away
    }
  }
}


